import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1749073668528-38ab64575f5d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxvZmZzaG9yZSUyMGNyYW5lJTIwb2NlYW4lMjBtYWNoaW5lcnl8ZW58MHx8fHwxNzg1NTI4MDk3fDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 150);
    return () => clearTimeout(t);
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden blueprint-grid"
    >
      {/* Right-side spotlighted image */}
      <motion.div
        style={{ y: imgY }}
        className="absolute top-0 right-0 h-full w-full md:w-[46%] z-0"
      >
        <div className="relative h-full w-full overflow-hidden border-l border-white/10">
          <motion.img
            style={{ scale: imgScale }}
            src={HERO_IMG}
            alt="Offshore crane servicing an oil rig at sea"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050B14] via-[#050B14]/30 to-transparent md:from-[#050B14]/60" />
          <div className="absolute inset-0 bg-[#050B14]/30" />
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 w-full px-6 md:px-12 pt-36 md:pt-44 pb-16 flex flex-col min-h-screen justify-between"
      >
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: ready ? 1 : 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent">
              [ Offshore Crane Operations ]
            </span>
            <span className="hidden md:block h-px w-24 bg-white/20" />
            <span className="hidden md:block font-mono text-xs tracking-[0.2em] uppercase text-[#64748B]">
              North Sea · Gulf · West Africa
            </span>
          </motion.div>

          <h1 aria-label="We move the immovable." className="font-display leading-[0.82] tracking-tighter text-6xl sm:text-7xl md:text-8xl lg:text-[9.5vw]">
            <MaskedLines
              active={ready}
              lines={["We move", "the", "immovable."]}
              delayBase={0.15}
            />
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mt-16">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: ready ? 1 : 0, y: ready ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="md:col-span-5 text-lg md:text-xl font-light text-[#94A3B8] leading-relaxed"
          >
            Certified offshore crane operators, riggers and heavy-lift crews for
            platforms, vessels and subsea projects — where precision is not
            optional and downtime is not an option.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: ready ? 1 : 0, y: ready ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="md:col-span-4 md:col-start-9 flex flex-col gap-4"
          >
            <button
              data-testid="hero-cta"
              onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center justify-between bg-accent text-[#050B14] font-mono text-sm tracking-[0.15em] uppercase px-6 py-4 hover:bg-white transition-colors duration-300"
            >
              Request a crew
              <span className="group-hover:translate-x-1 transition-transform duration-300" aria-hidden>→</span>
            </button>
            <button
              data-testid="hero-secondary"
              onClick={() => document.querySelector("#capabilities")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center justify-between border border-white/15 text-white font-mono text-sm tracking-[0.15em] uppercase px-6 py-4 hover:border-accent hover:text-accent transition-colors duration-300"
            >
              View capabilities
              <span aria-hidden>↓</span>
            </button>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
